/**
 * Generates temporary Appointment objects for offline booking.
 * Supports single and recurring appointments.
 *
 * @author Milton Filho
 */
import moment from 'moment';
import {
  Appointment,
  AppointmentService,
  AppointmentStatus,
  AppointmentStatusName,
  AppointmentType,
} from 'types/types';
import { BookAppointmentActionPayload } from 'modules/appointments/actions';

const RECURRENCE_WEEKS: Record<string, number> = {
  Weekly: 1,
  'Bi-Weekly': 2,
  '2 weeks': 2,
};

const createTempAppointment = (
  dateTime: moment.Moment,
  tempId: number,
  payload: BookAppointmentActionPayload,
  therapistId: number,
): Appointment => ({
  id: tempId,
  appDatetime: dateTime.toISOString(),
  appStatus: AppointmentStatusName.Scheduled,
  appType: AppointmentType.Ongoing,
  arrivedAt: '',
  allowedAmount: '0',
  amountPaid: '0',
  appCount: 0,
  appFee: null,
  cancellationFee: null,
  cancellationReason: null,
  caseId: payload.caseId || 0,
  duration: payload.duration,
  patientId: payload.patientId,
  patientResponsibility: '0',
  service: payload.type as AppointmentService,
  status: AppointmentStatus.Pending,
  stripePiSecret: null,
  techFee: '0',
  therapistId: therapistId,
  visitNumber: 0,
});

export const generateTempAppointments = (
  payload: BookAppointmentActionPayload,
  startTempId: number,
  therapistId: number,
): Appointment[] => {
  const appointments: Appointment[] = [];
  const startDate = moment(payload.dateTime);

  if (!payload.recurrence || payload.recurrence === 'Once') {
    appointments.push(
      createTempAppointment(startDate, startTempId, payload, therapistId),
    );
    return appointments;
  }

  const intervalWeeks = RECURRENCE_WEEKS[payload.recurrence] || 1;
  const endDate = payload.recursUntil ? moment(payload.recursUntil) : null;

  let currentDate = startDate.clone();
  let tempId = startTempId;

  do {
    appointments.push(
      createTempAppointment(currentDate, tempId, payload, therapistId),
    );
    tempId--;

    if (!endDate) break;
    currentDate = currentDate.clone().add(intervalWeeks, 'weeks');
  } while (!currentDate.isAfter(endDate));

  return appointments;
};
